import { useStableValue } from './useStable';
import { useEffect, useState } from 'react';

type ModifierKey = 'ctrl' | 'alt' | 'meta' | 'shift';

type UseKeyDownOptions = {
    key: string | string[] | ((event: KeyboardEvent) => boolean);
    modifiers?: ModifierKey[];
};

export function useKeyDown(options: UseKeyDownOptions): boolean {
    const { key, modifiers } = useStableValue(options);

    const [keyDown, setKeyDown] = useState<string | null>(null);

    useEffect(() => {
        function handleKeydownEvent(event: KeyboardEvent) {
            if (!areAllModifiersPressed(event, modifiers)) {
                return;
            }

            switch (typeof key) {
                case 'function': {
                    if (!key(event)) {
                        return;
                    }

                    break;
                }
                case 'string': {
                    if (event.key !== key) {
                        return;
                    }

                    break;
                }
                default: {
                    // `key` is string[]
                    if (!key.includes(event.key)) {
                        return;
                    }

                    break;
                }
            }

            setKeyDown(event.key);
        }

        function handleKeyupEvent(event: KeyboardEvent) {
            setKeyDown((down) => {
                if (event.key === down) {
                    return null;
                }

                return down;
            });
        }

        document.addEventListener('keydown', handleKeydownEvent);
        document.addEventListener('keyup', handleKeyupEvent);

        return () => {
            document.removeEventListener('keydown', handleKeydownEvent);
            document.removeEventListener('keyup', handleKeyupEvent);
        };
    }, [key, modifiers]);

    return keyDown !== null;
}

function areAllModifiersPressed(event: KeyboardEvent, modifiers?: ModifierKey[]): boolean {
    if (typeof modifiers === 'undefined' || modifiers.length === 0) {
        return true;
    }

    if (modifiers.includes('alt') && !event.altKey) {
        return false;
    }

    if (modifiers.includes('ctrl') && !event.ctrlKey) {
        return false;
    }

    if (modifiers.includes('meta') && !event.metaKey) {
        return false;
    }

    if (modifiers.includes('shift') && !event.shiftKey) {
        return false;
    }

    return true;
}
